import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box, Sheet, Typography, Button, List, ListItem, ListItemContent,
  Chip, CircularProgress, Divider, Snackbar, IconButton,
} from '@mui/joy';
import { LayoutContext } from '../context';
import { apiGetSessions, apiRevokeSession, apiRevokeAllSessions } from '../auth';

export default function SessionsPage() {
  const navigate = useNavigate();
  const { account } = useContext(LayoutContext);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState({ open: false, color: 'success', msg: '' });

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiGetSessions();
      setSessions(data?.sessions || []);
    } catch (e) {
      setSnack({ open: true, color: 'danger', msg: 'Не удалось загрузить сеансы' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revokeOne = async (id) => {
    setBusy(true);
    try {
      await apiRevokeSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      setSnack({ open: true, color: 'success', msg: 'Сеанс завершён' });
    } catch (e) {
      setSnack({ open: true, color: 'danger', msg: 'Не удалось завершить сеанс' });
    } finally {
      setBusy(false);
    }
  };

  const revokeAll = async () => {
    setBusy(true);
    try {
      await apiRevokeAllSessions(false);
      setSnack({ open: true, color: 'success', msg: 'Остальные сеансы завершены' });
      await load();
    } catch (e) {
      setSnack({ open: true, color: 'danger', msg: 'Не удалось завершить сеансы' });
    } finally {
      setBusy(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('ru-RU');
    } catch {
      return iso;
    }
  };

  return (
    <Box sx={{ maxWidth: 680, mx: 'auto', p: { xs: 2, sm: 3 }, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button variant="plain" color="neutral" onClick={() => navigate(-1)}>← Назад</Button>
        <Typography level="h3">Устройства и сеансы</Typography>
      </Box>

      <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {sessions.length === 0 && (
              <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                Активных сеансов нет
              </Typography>
            )}
            {sessions.map((s, i) => (
              <Box key={s.id}>
                {i > 0 && <Divider />}
                <ListItem
                  endAction={
                    !s.is_current && (
                      <Button size="sm" variant="soft" color="danger" disabled={busy} onClick={() => revokeOne(s.id)}>
                        Завершить
                      </Button>
                    )
                  }
                >
                  <ListItemContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography level="title-sm">{s.device_name || 'Неизвестное устройство'}</Typography>
                      {s.is_current && <Chip size="sm" color="success" variant="soft">текущее</Chip>}
                    </Box>
                    <Typography level="body-xs" textColor="text.tertiary">
                      {s.ip_address || '—'} · активность: {fmt(s.last_active_at)}
                    </Typography>
                  </ListItemContent>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Sheet>

      <Button variant="outlined" color="danger" disabled={busy || loading} onClick={revokeAll} sx={{ mt: 2 }}>
        Завершить все остальные сеансы
      </Button>

      <Snackbar
        open={snack.open}
        color={snack.color}
        variant="soft"
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack.msg}
      </Snackbar>
    </Box>
  );
}